import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { AppService } from 'common-cloudstudio';

export interface TableColumn {
  Key: string;     // nombre del campo en rows
  Title: string;  // encabezado visible
}

export interface TableData {
  Columns: TableColumn[];
  Rows: any[];
}

@Component({
  selector: 'app-simplified-table',
  templateUrl: './simplified-table.component.html',
  styleUrls: ['./simplified-table.component.scss']
})
export class SimplifiedTableComponent implements OnInit, OnChanges {
  @Input() data: TableData;

  _currentPageNumber: number = 1;
  _totalPageCount: number = 1;
  _itemsPerPage: number = 10;
  _itemsPerPageOptions: number[] = [5, 10, 25, 50, 100];
  _filteredRows: any[] = [];
  _displayedRows: any[] = [];
  
  _PreviousHidden: boolean = false;
  _NextHidden: boolean = false;

  constructor(public _appService: AppService) {}

  ngOnInit(): void {
    this.initializePagination();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && changes['data'].currentValue) {
      this.initializePagination();
    }
  }

  /**
   * Inicializa la paginación con los datos actuales
   */
  initializePagination(): void {
    if (!this.data?.Rows) {
      this._filteredRows = [];
      this._displayedRows = [];
      this._totalPageCount = 1;
      this._currentPageNumber = 1;
      return;
    }

    this._filteredRows = [...this.data.Rows];
    this.calculateTotalPages();
    this.updateDisplayedRows();
    this.updateNavigationButtons();
  }

  /**
   * Calcula el número total de páginas
   */
  calculateTotalPages(): void {
    this._totalPageCount = Math.ceil(this._filteredRows.length / this._itemsPerPage);
    if (this._currentPageNumber > this._totalPageCount) {
      this._currentPageNumber = this._totalPageCount || 1;
    }
  }

  /**
   * Actualiza las filas que se muestran en la página actual
   */
  updateDisplayedRows(): void {
    const startIndex = (this._currentPageNumber - 1) * this._itemsPerPage;
    const endIndex = startIndex + this._itemsPerPage;
    this._displayedRows = this._filteredRows.slice(startIndex, endIndex);
  }

  /**
   * Actualiza el estado de los botones de navegación
   */
  updateNavigationButtons(): void {
    this._PreviousHidden = this._currentPageNumber > 1;
    this._NextHidden = this._currentPageNumber < this._totalPageCount;
  }

  /**
   * Maneja el cambio en el número de elementos por página
   */
  onItemsPerPageChange(newValue: number): void {
    this._itemsPerPage = newValue;
    this._currentPageNumber = 1; // Volver a la primera página
    this.calculateTotalPages();
    this.updateDisplayedRows();
    this.updateNavigationButtons();
  }

  /**
   * Navega entre páginas
   * @param direction Dirección de navegación (-1 para anterior, 1 para siguiente)
   * @param goToExtreme Si es true, va a la primera o última página
   */
  movePage(direction: number, goToExtreme: boolean = false): void {
    if (goToExtreme) {
      if (direction === -1) {
        this._currentPageNumber = 1;
      } else {
        this._currentPageNumber = this._totalPageCount;
      }
    } else {
      const newPage = this._currentPageNumber + direction;
      if (newPage >= 1 && newPage <= this._totalPageCount) {
        this._currentPageNumber = newPage;
      }
    }

    this.updateDisplayedRows();
    this.updateNavigationButtons();
  }



  /**
   * Obtiene información de paginación para mostrar
   */
  getPaginationInfo(): string {
    if (!this._filteredRows.length) {
      return '0 de 0 registros';
    }

    const startIndex = (this._currentPageNumber - 1) * this._itemsPerPage + 1;
    const endIndex = Math.min(this._currentPageNumber * this._itemsPerPage, this._filteredRows.length);
    const totalRecords = this._filteredRows.length;

    return `${startIndex}-${endIndex} de ${totalRecords} registros`;
  }

  /**
   * Va a una página específica
   */
  goToPage(pageNumber: number): void {
    if (pageNumber >= 1 && pageNumber <= this._totalPageCount) {
      this._currentPageNumber = pageNumber;
      this.updateDisplayedRows();
      this.updateNavigationButtons();
    }
  }

  /**
   * Obtiene el rango de páginas para mostrar en la paginación
   */
  getPageRange(): number[] {
    const range: number[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, this._currentPageNumber - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(this._totalPageCount, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }

    return range;
  }
}
